import { useCallback, useEffect, useState } from 'react';
import I18n from 'I18n';
import FormattedMessage from 'I18n/components/FormattedMessage';
import FormattedJSXMessage from 'I18n/components/FormattedJSXMessage';
import { H2 } from 'foundations-components/transitional/components/H2';
import { Small } from 'foundations-components/transitional/components/Small';
import { LoadingButton } from 'foundations-components/transitional/components/LoadingButton';
import { DialogBody } from 'foundations-components/transitional/components/DialogBody';
import { DialogCloseButton } from 'foundations-components/transitional/components/DialogCloseButton';
import { DialogFooter } from 'foundations-components/transitional/components/DialogFooter';
import { DialogHeader } from 'foundations-components/transitional/components/DialogHeader';
import { Modal } from 'foundations-components/transitional/components/Modal';
import { FormControl } from 'foundations-components/transitional/components/FormControl';
import { Link } from 'foundations-components/transitional/components/Link';
import { Button } from 'foundations-components/transitional/components/Button';
import { Select } from 'foundations-components/components/Select';
import { FloatingAlertStore } from 'foundations-components/transitional/components/FloatingAlertList';
import { captureNetworkError } from 'growth-onboarding-reliability/utils/raven';
import { getNavConfig, setDefaultHome } from 'setup-guide-api/api/navConfigApi';
import { changeEnrolledInUserGuide } from 'onboarding-settings-client/api/enrollment';
import { usePortalId } from '../../hooks/hub';
import { useTracker } from '../../hooks/tracking';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const handleHomepageModalError = error => {
  FloatingAlertStore.addAlert({
    message: I18n.text('setup-guide-components.updateYourHomepageModal.errorAlertMessage'),
    titleText: I18n.text('setup-guide-components.updateYourHomepageModal.errorAlertTitle'),
    type: 'danger'
  });
  captureNetworkError(error);
};
export default function ChangeDefaultHomepageModal({
  onClose,
  description,
  heading
}) {
  const [optionsState, setOptionsState] = useState({
    isLoading: true,
    options: []
  });
  const portalId = usePortalId();
  const {
    trackUserInteraction,
    trackSectionView
  } = useTracker();
  const [homepageState, setHomePageState] = useState({
    isSettingHomepage: false,
    selectedHomepage: ''
  });
  useEffect(() => {
    getNavConfig().then(navConfig => {
      setOptionsState({
        isLoading: false,
        options: navConfig.filter(configOption => configOption.canBeHomePage)
      });
    }).catch(handleHomepageModalError);
  }, []);
  useEffect(() => {
    trackSectionView('modal', 'change default homepage modal');
  }, [trackSectionView]);
  const handleCloseClick = () => {
    trackUserInteraction('click change default homepage modal close button');
    onClose();
  };
  const handleSetHomepageClick = useCallback(() => {
    if (!homepageState.isSettingHomepage) {
      setHomePageState(Object.assign({}, homepageState, {
        isSettingHomepage: true
      }));
      const {
        options
      } = optionsState;
      const {
        selectedHomepage
      } = homepageState;
      const selectedHomepageObject = options && options.find(option => option.id === selectedHomepage);
      if (selectedHomepageObject) {
        setDefaultHome(selectedHomepage).then(() => {
          // Unenroll user from user guide after they select a new homepage
          return changeEnrolledInUserGuide({
            isEnrolled: false
          }).catch(captureNetworkError);
        }).then(() => {
          setHomePageState(Object.assign({}, homepageState, {
            isSettingHomepage: false
          }));
          FloatingAlertStore.addAlert({
            message: I18n.text('setup-guide-components.updateYourHomepageModal.successAlertMessage'),
            titleText: I18n.text('setup-guide-components.updateYourHomepageModal.successAlertTitle'),
            type: 'success'
          });
          trackUserInteraction('change default homepage', selectedHomepage);
          onClose(selectedHomepageObject);
        }).catch(handleHomepageModalError);
      } else {
        setHomePageState(Object.assign({}, homepageState, {
          isSettingHomepage: false
        }));
      }
    }
  }, [homepageState, onClose, optionsState, trackUserInteraction]);
  const {
    selectedHomepage
  } = homepageState;
  return /*#__PURE__*/_jsxs(Modal, {
    "data-test-id": "change-homepage-modal",
    children: [/*#__PURE__*/_jsx(DialogHeader, {
      children: /*#__PURE__*/_jsx(H2, {
        children: heading || /*#__PURE__*/_jsx(FormattedMessage, {
          message: "setup-guide-components.updateYourHomepageModal.heading"
        })
      })
    }), /*#__PURE__*/_jsx(DialogCloseButton, {
      onClick: handleCloseClick
    }), /*#__PURE__*/_jsxs(DialogBody, {
      children: [description || /*#__PURE__*/_jsx(FormattedMessage, {
        message: "setup-guide-components.updateYourHomepageModal.description"
      }), /*#__PURE__*/_jsx(FormControl, {
        label: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "setup-guide-components.updateYourHomepageModal.dropdownLabel"
        }),
        marginBlockEnd: "200",
        marginBlockStart: "200",
        tooltip: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "setup-guide-components.updateYourHomepageModal.dropdownInfo"
        }),
        children: /*#__PURE__*/_jsx(Select, {
          disabled: optionsState.isLoading,
          onSelectedOptionChange: ({
            value
          }) => {
            setHomePageState(Object.assign({}, homepageState, {
              selectedHomepage: value || ''
            }));
          },
          options: optionsState.options.map(configOption => ({
            value: configOption.id,
            text: configOption.nonContextualLabel
          })),
          searchable: true,
          selectedItemsPlaceholder: I18n.text('setup-guide-components.updateYourHomepageModal.dropdownPlaceholder')
        })
      }), /*#__PURE__*/_jsx(Small, {
        children: /*#__PURE__*/_jsx(FormattedJSXMessage, {
          elements: {
            Link
          },
          message: "setup-guide-components.updateYourHomepageModal.changeLater_jsx",
          options: {
            href: `/settings/${portalId}/user-preferences/profile`,
            external: true
          }
        })
      })]
    }), /*#__PURE__*/_jsxs(DialogFooter, {
      children: [/*#__PURE__*/_jsx(LoadingButton, {
        disabled: optionsState.isLoading || selectedHomepage === '',
        loading: homepageState.isSettingHomepage,
        onClick: handleSetHomepageClick,
        use: "primary",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "setup-guide-components.updateYourHomepageModal.confirmButton"
        })
      }), /*#__PURE__*/_jsx(Button, {
        onClick: handleCloseClick,
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "setup-guide-components.updateYourHomepageModal.cancelButton"
        })
      })]
    })]
  });
}